import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/App";
import { cargoAPI, jetAPI } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";

import { 
  AddCargoForm, 
  AddJetForm, 
  initialCargoForm, 
  initialJetForm 
} from "./listing";

/**
 * Add Listing Page
 * 
 * Container component for creating cargo and jet listings.
 * Uses tabbed interface to switch between listing types.
 */
export default function AddListing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listingType, setListingType] = useState("cargo");
  const [loading, setLoading] = useState(false);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  
  const [cargoForm, setCargoForm] = useState(initialCargoForm(user));
  const [jetForm, setJetForm] = useState(initialJetForm(user));

  const handleCargoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await cargoAPI.create({
        ...cargoForm,
        departure_date: format(cargoForm.departure_date, "yyyy-MM-dd"),
        available_capacity: parseFloat(cargoForm.available_capacity),
        price_per_kg: parseFloat(cargoForm.price_per_kg),
        space_length: cargoForm.space_length ? parseFloat(cargoForm.space_length) : null,
        space_width: cargoForm.space_width ? parseFloat(cargoForm.space_width) : null,
        space_height: cargoForm.space_height ? parseFloat(cargoForm.space_height) : null,
        max_weight: cargoForm.max_weight ? parseFloat(cargoForm.max_weight) : null,
        eta_hours: cargoForm.eta_hours ? parseInt(cargoForm.eta_hours) : null,
      });
      toast.success("Cargo listing created!");
      navigate("/partner/listings");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create listing");
    } finally {
      setLoading(false);
    }
  };

  const handleJetSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await jetAPI.create({
        ...jetForm,
        departure_date: format(jetForm.departure_date, "yyyy-MM-dd"),
        available_seats: parseInt(jetForm.available_seats),
        price_per_seat: parseFloat(jetForm.price_per_seat),
        amenities: selectedAmenities,
      });
      toast.success("Jet listing created!");
      navigate("/partner/listings");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create listing");
    } finally {
      setLoading(false);
    }
  };

  const toggleAmenity = (amenity) => {
    setSelectedAmenities(prev => 
      prev.includes(amenity) 
        ? prev.filter(a => a !== amenity)
        : [...prev, amenity]
    );
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-black tracking-tight">Add New Listing</h1>

      <Tabs value={listingType} onValueChange={setListingType}>
        <TabsList className="rounded-none">
          <TabsTrigger value="cargo" className="rounded-none" data-testid="add-cargo-tab">
            Cargo Space
          </TabsTrigger>
          <TabsTrigger value="jet" className="rounded-none" data-testid="add-jet-tab">
            Private Jet
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cargo" className="mt-6">
          <AddCargoForm
            form={cargoForm}
            setForm={setCargoForm}
            onSubmit={handleCargoSubmit}
            loading={loading}
          />
        </TabsContent>

        <TabsContent value="jet" className="mt-6">
          <AddJetForm
            form={jetForm}
            setForm={setJetForm}
            onSubmit={handleJetSubmit}
            loading={loading}
            selectedAmenities={selectedAmenities}
            toggleAmenity={toggleAmenity}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
